import React, { Fragment, useMemo } from 'react';
import { Box } from '@mui/material';
import { shouldHideProductElement } from '@shared/helpers/element.helpers';
import { isProductCategoryElement } from '@shared/helpers/element-category.helpers';
import {
  isChildOf,
  isProductElement,
} from '@shared/helpers/recursive-element.helpers';
import { IElement, IProductElement } from '@shared/models/project.model';
import { ProductBulkEditForm } from '@features/products/ProductBulkEditForm';
import { SidePanel } from '@ui/layout';
import { useElementChildren } from '@client/hooks/use-elements.hooks';

export const useGeneratedProductElementChildren = (element: IElement) => {
  const children = useElementChildren(element);
  const filteredChildren = useMemo(
    () =>
      children.filter((child): child is IProductElement => {
        // Exclude the element itself
        if (child.id === element.id) return false;

        // we only show children of element
        if (!isChildOf(element, child)) return false;

        // if this is true, we should hide it!
        if (shouldHideProductElement(child)) return false;

        return isProductElement(child);
      }),
    [children, element],
  );
  return filteredChildren;
};

type Props = {
  generatedProductElementChildren: IProductElement[];
  element: IElement;
};

export const GeneratedElementChildrenSettings: React.FC<Props> = ({
  element,
  generatedProductElementChildren,
}) => {
  const productCategoryElement = isProductCategoryElement(element)
    ? element
    : undefined;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {generatedProductElementChildren.map((item) => {
        return (
          <Fragment key={item.id}>
            <ProductBulkEditForm
              productCategoryElement={productCategoryElement}
              productElements={[item]}
              showGenericProductName
            />
            <SidePanel.Content.Divider />
          </Fragment>
        );
      })}
    </Box>
  );
};
